/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

const OPPORTUNITY_NAME_MAX_LENGTH = 120

/** Build Opportunity Name from Anlasstyp + Account Name (space-separated). Form-only Anlasstyp is not persisted. */
export const buildOpportunityName = (anlasstyp?: string | null, accountName?: string | null): string => {
    const occasion = (anlasstyp ?? '').trim()
    const account = (accountName ?? '').trim()

    // Require Anlasstyp so account selection alone does not overwrite Opportunity Name.
    if (!occasion) {
        return ''
    }
    if (!account) {
        return occasion.slice(0, OPPORTUNITY_NAME_MAX_LENGTH)
    }

    return `${occasion} ${account}`.slice(0, OPPORTUNITY_NAME_MAX_LENGTH)
}

/**
 * Best-effort reverse of buildOpportunityName for edit forms (Anlasstyp is not stored on Lead).
 * Returns the name prefix before the account, or empty when it cannot be inferred.
 */
export const parseAnlasstypFromOpportunityName = (
    name?: string | null,
    accountName?: string | null
): string => {
    const opportunityName = (name ?? '').trim()
    const account = (accountName ?? '').trim()
    if (!opportunityName || !account) {
        return ''
    }
    if (opportunityName === account) {
        return ''
    }
    const suffix = ` ${account}`
    if (opportunityName.endsWith(suffix)) {
        return opportunityName.slice(0, opportunityName.length - suffix.length).trim()
    }
    return ''
}

/** True when the current name is empty or still matches the last auto-generated value. */
export const canAutoUpdateOpportunityName = (
    currentName: string | null | undefined,
    lastAutoGeneratedName: string
): boolean => {
    const normalized = (currentName ?? '').trim()
    return !normalized || normalized === lastAutoGeneratedName
}
